/**
 * Asset tab "Lifecycle": device history (activated, commissioned, maintenance,
 * decommissioned, ...) with time, operator and source, newest first. Filterable
 * by event type; further pages are loaded on demand.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";

import { authenticationAtom } from "../../State";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  LIFECYCLE_EVENT_TYPES,
  useAssetLifecycleEvents,
  type LifecycleEvent,
} from "./useAssetLifecycleEvents";

interface Properties {
  asset: any;
  /** The page's date formatter, so the tab renders times like the rest of the page. */
  formatDateTime: (value: unknown) => string;
}

const KNOWN_SOURCES = ["app", "portal", "system"];

function operatorLabel(by: LifecycleEvent["by"]): string | null {
  return by?.name || by?.email || null;
}

export default function AssetLifecyclePanel({ asset, formatDateTime }: Properties) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { events, hasMore, loading, loadingMore, error, loadMore, retry } =
    useAssetLifecycleEvents(authentication?.customer, asset?._id, selectedTypes);

  const toggleType = (type: string) =>
    setSelectedTypes((current) =>
      current.includes(type)
        ? current.filter((entry) => entry !== type)
        : [...current, type],
    );

  const eventLabel = (type: string) =>
    t(`assetPropertiesPanel.lifecycle.event.${type}`, { defaultValue: type });

  const sourceCell = (source: string) => {
    if (source === "derived") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" tabIndex={0} data-testid="lifecycle-derived-badge">
                {t("assetPropertiesPanel.lifecycle.source.derived")}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {t("assetPropertiesPanel.lifecycle.derivedHint")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return KNOWN_SOURCES.includes(source)
      ? t(`assetPropertiesPanel.lifecycle.source.${source}`)
      : source;
  };

  // A first-page failure replaces the table; a failed "load more" keeps the loaded rows.
  const firstPageFailed = error !== null && events.length === 0;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div
        role="group"
        aria-label={t("assetPropertiesPanel.lifecycle.filterLabel")}
        className="flex flex-wrap items-center gap-2"
      >
        <Button
          type="button"
          size="sm"
          variant={selectedTypes.length === 0 ? "default" : "outline"}
          aria-pressed={selectedTypes.length === 0}
          onClick={() => setSelectedTypes([])}
        >
          {t("assetPropertiesPanel.lifecycle.filterAll")}
        </Button>
        {LIFECYCLE_EVENT_TYPES.map((type) => {
          const active = selectedTypes.includes(type);
          return (
            <Button
              key={type}
              type="button"
              size="sm"
              variant={active ? "default" : "outline"}
              aria-pressed={active}
              onClick={() => toggleType(type)}
            >
              {eventLabel(type)}
            </Button>
          );
        })}
      </div>

      {loading ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t("assetPropertiesPanel.lifecycle.loading")}
        </p>
      ) : firstPageFailed ? (
        <div role="alert" className="flex flex-col items-start gap-2 text-sm">
          <p>
            {error === "forbidden"
              ? t("assetPropertiesPanel.lifecycle.errorForbidden")
              : t("assetPropertiesPanel.lifecycle.errorGeneric")}
          </p>
          {error !== "forbidden" && (
            <Button type="button" size="sm" variant="outline" onClick={retry}>
              {t("assetPropertiesPanel.lifecycle.retry")}
            </Button>
          )}
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {selectedTypes.length > 0
            ? t("assetPropertiesPanel.lifecycle.emptyFiltered")
            : t("assetPropertiesPanel.lifecycle.empty")}
        </p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("assetPropertiesPanel.lifecycle.columnTime")}</TableHead>
                <TableHead>{t("assetPropertiesPanel.lifecycle.columnEvent")}</TableHead>
                <TableHead>{t("assetPropertiesPanel.lifecycle.columnOperator")}</TableHead>
                <TableHead>{t("assetPropertiesPanel.lifecycle.columnSource")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(event.at)}
                  </TableCell>
                  <TableCell>{eventLabel(event.type)}</TableCell>
                  <TableCell>
                    {operatorLabel(event.by) ??
                      t("assetPropertiesPanel.lifecycle.noOperator")}
                  </TableCell>
                  <TableCell>{sourceCell(event.source)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {error !== null && (
            <div role="alert" className="flex items-center gap-2 text-sm">
              <span>{t("assetPropertiesPanel.lifecycle.errorGeneric")}</span>
              <Button type="button" size="sm" variant="outline" onClick={loadMore}>
                {t("assetPropertiesPanel.lifecycle.retry")}
              </Button>
            </div>
          )}
          {hasMore && error === null && (
            <div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={loadingMore}
                onClick={loadMore}
              >
                {loadingMore
                  ? t("assetPropertiesPanel.lifecycle.loadingMore")
                  : t("assetPropertiesPanel.lifecycle.loadMore")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
